import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActionItemsPanel = ({ actionItems, onActionClick }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-border-light border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'AlertCircle';
      case 'low': return 'Info';
      default: return 'Circle';
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'incomplete': return 'Play';
      case 'failed': return 'RotateCcw';
      case 'review': return 'BookOpen';
      case 'practice': return 'PenTool';
      case 'achievement': return 'Award';
      default: return 'ArrowRight';
    }
  };

  const groupedItems = actionItems.reduce((acc, item) => {
    if (!acc[item.priority]) {
      acc[item.priority] = [];
    }
    acc[item.priority].push(item);
    return acc;
  }, {});

  const priorityOrder = ['high', 'medium', 'low'];

  return (
    <div className="p-6 border rounded-lg bg-surface border-border">
      <h3 className="flex items-center mb-6 space-x-2 text-lg font-semibold font-heading text-text-primary">
        <Icon name="CheckSquare" size={20} />
        <span>Recommended Actions</span>
      </h3>

      <div className="space-y-6">
        {priorityOrder.map((priority) => {
          const items = groupedItems[priority] || [];
          if (items.length === 0) return null;

          return (
            <div key={priority}>
              <div className="flex items-center mb-3 space-x-2">
                <Icon name={getPriorityIcon(priority)} size={16} className={getPriorityColor(priority).split(' ')[0]} />
                <h4 className="font-medium capitalize text-text-primary">{priority} Priority</h4>
                <span className="text-sm text-text-secondary">({items.length})</span>
              </div>

              <div className="space-y-3">
                {items.map((item) => (
                  <div 
                    key={item.id} 
                    className={`border rounded-lg p-4 ${getPriorityColor(priority)}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-surface">
                          <Icon name={getActionIcon(item.type)} size={16} className="text-text-primary" />
                        </div>
                        <div>
                          <h5 className="font-medium text-text-primary">{item.title}</h5>
                          <p className="mt-1 text-sm text-text-secondary">{item.description}</p>
                        </div>
                      </div>
                      {item.estimatedTime && (
                        <span className="px-2 py-1 text-xs rounded text-text-secondary bg-surface">
                          {item.estimatedTime}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-xs text-text-secondary">
                        {item.chapter && (
                          <span className="flex items-center space-x-1">
                            <Icon name="BookOpen" size={12} />
                            <span>{item.chapter}</span>
                          </span>
                        )}
                        {item.dueDate && (
                          <span className="flex items-center space-x-1">
                            <Icon name="Calendar" size={12} />
                            <span>Due {item.dueDate}</span>
                          </span>
                        )}
                        {item.attempts && (
                          <span className="flex items-center space-x-1">
                            <Icon name="RotateCcw" size={12} />
                            <span>{item.attempts} attempts</span>
                          </span>
                        )}
                      </div>

                      <Button
                        variant={priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'primary'}
                        size="sm"
                        iconName={getActionIcon(item.type)}
                        onClick={() => onActionClick(item)}
                      >
                        {item.actionText || 'Start'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {actionItems.length === 0 && (
          <div className="py-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-success/10">
              <Icon name="CheckCircle2" size={32} className="text-success" />
            </div>
            <h4 className="mb-2 font-medium text-text-primary">All Caught Up!</h4>
            <p className="text-sm text-text-secondary">
              Great job! You have no pending action items. Keep up the excellent work!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionItemsPanel;